/** Reauth + email-confirm dialogs for account self-delete. */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useT } from '@/i18n/useT';
import {
  SETTINGS_ACCOUNT_DELETE_CONFIRM_INPUT_TEST_ID,
  SETTINGS_ACCOUNT_DELETE_CONFIRM_TEST_ID,
  SETTINGS_ACCOUNT_DELETE_REAUTH_PASSWORD_TEST_ID,
  SETTINGS_ACCOUNT_DELETE_START_TEST_ID,
} from '@/features/settings/constants/settingsTestIds';

/**
 * @param {object} props
 * @param {boolean} props.confirmOpen
 * @param {boolean} props.reauthPasswordOpen
 * @param {string} props.reauthPassword
 * @param {(value: string) => void} props.onReauthPasswordChange
 * @param {boolean} props.deleteInProgress
 * @param {() => void} props.onBeginDelete
 * @param {() => void} props.onSubmitReauthPassword
 * @param {(typedEmail: string) => void} props.onConfirmDelete
 * @param {() => void} props.onCancel
 */
export default function AccountDeleteDialogs({
  confirmOpen,
  reauthPasswordOpen,
  reauthPassword,
  onReauthPasswordChange,
  deleteInProgress,
  onBeginDelete,
  onSubmitReauthPassword,
  onConfirmDelete,
  onCancel,
}) {
  const t = useT();
  const [confirmEmail, setConfirmEmail] = useState('');

  const handleConfirmClose = () => {
    setConfirmEmail('');
    onCancel();
  };

  return (
    <>
      <Button
        color="error"
        variant="outlined"
        size="small"
        disabled={deleteInProgress}
        data-testid={SETTINGS_ACCOUNT_DELETE_START_TEST_ID}
        onClick={onBeginDelete}
        sx={{ mt: 3 }}
      >
        {t('settings.account.delete.button', 'Delete account')}
      </Button>

      <Dialog
        open={reauthPasswordOpen}
        onClose={deleteInProgress ? undefined : onCancel}
        role="alertdialog"
      >
        <DialogTitle>{t('settings.account.delete.reauthTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('settings.account.delete.reauthBody')}
          </DialogContentText>
          <TextField
            fullWidth
            type="password"
            autoComplete="current-password"
            label={t('settings.account.delete.reauthPasswordLabel')}
            value={reauthPassword}
            onChange={(e) => onReauthPasswordChange(e.target.value)}
            disabled={deleteInProgress}
            inputProps={{
              'data-testid': SETTINGS_ACCOUNT_DELETE_REAUTH_PASSWORD_TEST_ID,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} color="inherit" disabled={deleteInProgress}>
            {t('confirm.cancel')}
          </Button>
          <Button
            onClick={onSubmitReauthPassword}
            color="error"
            variant="contained"
            disabled={deleteInProgress || !reauthPassword}
          >
            {t('settings.account.delete.reauthContinue')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={deleteInProgress ? undefined : handleConfirmClose}
        role="alertdialog"
      >
        <DialogTitle>{t('settings.account.delete.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('settings.account.delete.confirmBody')}
          </DialogContentText>
          <TextField
            fullWidth
            label={t('settings.account.delete.confirmEmailLabel')}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            disabled={deleteInProgress}
            inputProps={{
              'data-testid': SETTINGS_ACCOUNT_DELETE_CONFIRM_INPUT_TEST_ID,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmClose}
            color="inherit"
            disabled={deleteInProgress}
          >
            {t('confirm.cancel')}
          </Button>
          <Button
            onClick={() => onConfirmDelete(confirmEmail)}
            color="error"
            variant="contained"
            disabled={deleteInProgress || !confirmEmail.trim()}
            data-testid={SETTINGS_ACCOUNT_DELETE_CONFIRM_TEST_ID}
          >
            {t('settings.account.delete.confirmAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
